import type { FWDTemplateChartSeries, TemplateChartSeries } from '@/shared/types/charts';

export type ChartTabDefinition = {
  id: string;
  name: string;
  metadata: null | Record<string, unknown>;
  series: Array<TemplateChartSeries>;
};

/// V2
export type SeasonalChartWidgetV2 = {
  id: string;
  name: string;
  type: 'graph';
  tabs?: Array<ChartTabDefinition>;
  layout?: {
    height?: number;
  } | null;
  metadata: {
    graphType: 'seasonal';
    from: string;
  };
  series: Array<TemplateChartSeries>;
  updatedAt?: string;
};

export type FWDChartWidgetV2 = {
  id: string;
  name: string;
  type: 'graph';
  tabs?: Array<ChartTabDefinition>;
  layout?: {
    height?: number;
  } | null;
  metadata: {
    graphType: 'forwardSimple';
  };
  series: Array<FWDTemplateChartSeries>;
  updatedAt?: string;
};

export type SimpleChartWidgetV2 = {
  id: string;
  name: string;
  type: 'graph';
  tabs?: Array<ChartTabDefinition>;
  layout?: {
    height?: number;
  } | null;
  metadata: Record<string, string> | null;
  series: Array<TemplateChartSeries>;
  updatedAt?: string;
};

export type FeedWidgetV2 = {
  id: string;
  name: string;
  type: 'feed';
  layout?: {
    height?: number;
  } | null;
  metadata: {
    KeyCategory: string;
    KeyValue: string;
  };
  updatedAt?: string;
};
export type CSVTableWidgetV2 = {
  id: string;
  name: string;
  type: 'csvtable';
  layout?: {
    height?: number;
  } | null;
  metadata: null | Record<string, unknown>;
  fileName: string;
  groupName: string;
  updatedAt?: string;
};

export type MiniTableWidgetV2 = {
  id: string;
  name: string;
  type: 'minitable';
  layout?: {
    height?: number;
  } | null;
  metadata: null | Record<string, unknown>;
  tables: Array<{
    name: string;
    layout: {
      height?: number;
    } | null;
    rows: Array<
      | {
          columns: string[];
          metadata: null | Record<string, unknown>;
          layout: null | Record<string, unknown>;
          from: string;
        }
      | {
          symbol: {
            key: {
              Code: string;
              PeriodType: string;
              Period: string;
              TimeRef: string;
            };
            group: string;
          };
          diffSymbol?: {
            key: {
              Code: string;
              PeriodType: string;
              Period: string;
              TimeRef: string;
            };
            group?: string;
          };
          from: string;
          columns: string[];
          layout: null | Record<string, unknown>;
          metadata: null | Record<string, unknown>;
        }
    >;
  }>;
  updatedAt?: string;
};

export type TableRow = {
  shortTitle: string;
  symbol: {
    key: {
      Code: string;
      PeriodType: string;
      Period: string;
      TimeRef: string;
    };
    group: string;
  };
  diffSymbol?: {
    key: {
      Code: string;
      PeriodType: string;
      Period: string;
      TimeRef: string;
    };
    group: string;
  };
};

export type TableWidgetV2 = {
  id: string;
  name: string;
  type: 'table';
  layout?: {
    height?: number;
  } | null;
  metadata: null | Record<string, unknown>;
  sections: Array<{
    name: string;
    rows: TableRow[];
  }>;
  updatedAt?: string;
};

export type MultiGroupTableSymbolPropertyConfig = {
  name: string;
  label: string;
};

export type MultiGroupTableRowV2 = {
  shortTitle: string;
  symbolA: {
    key: {
      Code: string;
      PeriodType: string;
      Period: string;
      TimeRef: string;
    };
    group: string;
  };
  symbolAProperties: MultiGroupTableSymbolPropertyConfig[];
  symbolB: {
    key: {
      Code: string;
      PeriodType: string;
      Period: string;
      TimeRef: string;
    };
    group: string;
  };
  symbolBProperties: MultiGroupTableSymbolPropertyConfig[];
};

export type MultiGroupTableWidgetV2 = {
  id: string;
  name: string;
  type: 'multi_group_table';
  layout?: {
    height?: number;
  } | null;
  metadata: null | Record<string, unknown>;
  sections: Array<{
    name: string;
    rows: MultiGroupTableRowV2[];
  }>;
  updatedAt?: string;
};

export type SpotHistoryTableItem = {
  name: string;
  symbol: {
    key: {
      Code: string;
      PeriodType: string;
      Period: string;
      TimeRef: string;
    };
    group: string;
  };
};

export type SpotHistoryTableWidgetV2 = {
  id: string;
  name: string;
  type: 'spot_history_table';
  layout?: {
    height?: number;
  } | null;
  metadata: null | Record<string, unknown>;
  items: SpotHistoryTableItem[];
  updatedAt?: string;
};

export type TileBigNumber = {
  id: string;
  shortTitle: string;
  symbol: {
    key: {
      Code: string;
      PeriodType: string;
      Period: string;
      TimeRef: string;
    };
    group: string;
  };
};
export type TilesWidgetV2 = {
  id: string;
  name: string;
  layout?: {
    height?: number;
  } | null;
  metadata: null | Record<string, unknown>;
  type: 'tiles';
  tiles: TileBigNumber[];
  updatedAt?: string;
};

export type FilesWidgetV2 = {
  id: string;
  name: string;
  type: 'file_list';
  layout?: {
    height?: number;
  } | null;
  metadata: null | Record<string, unknown>;
  groupName: string;
  size?: number;
  updatedAt?: string;
};

export type StaticDataChartWidgetV2 = {
  id: string;
  name: string;
  type: 'graph';
  layout?: {
    height?: number;
    fullWidth?: boolean;
  } | null;
  metadata: {
    graphType: 'static';
  };
  tool_call_id?: string;
  chartConfig: Record<string, unknown>;
  updatedAt?: string;
};

export type WidgetV2 =
  | SeasonalChartWidgetV2
  | SimpleChartWidgetV2
  | FWDChartWidgetV2
  | FeedWidgetV2
  | CSVTableWidgetV2
  | MiniTableWidgetV2
  | TableWidgetV2
  | MultiGroupTableWidgetV2
  | SpotHistoryTableWidgetV2
  | FilesWidgetV2
  | StaticDataChartWidgetV2;
export type MarketAppWithTabs = { tabs: WidgetV2[]; id: string };
export type MarketApp = MarketAppWithTabs | TilesWidgetV2;
export type MarketApps = MarketApp[];

// Tab entry with id and content
export type TabEntry = {
  id: string;
  content: MarketApps;
  /** Last time this tab's content or the dashboard's own metadata changed. Rolls up to the dashboard level as the max across tabs. */
  updatedAt?: string;
};

// New nested structure types
// Legacy format: { [tabName: string]: MarketApps }
// New format: { [tabName: string]: TabEntry }
export type DashboardTab = {
  [tabName: string]: MarketApps | TabEntry;
};

export type NestedDashboardStructure = {
  [dashboardName: string]: DashboardTab;
};

// Legacy structure type
export type LegacyDashboardStructure = {
  content: MarketApps;
};

// Union type for backward compatibility
export type DashboardStructure = NestedDashboardStructure | LegacyDashboardStructure;

// Type guards for structure detection
export type DashboardResponse = LegacyDashboardStructure | NestedDashboardStructure;

export enum AppType {
  Spot = 'spot',
  FWD = 'forward',
  Seasonal = 'seasonal',
  Table = 'table',
  Tiles = 'tiles',
  Feed = 'feed',
  CSVTable = 'csvtable',
  Minitable = 'minitable',
  SpotHistoryTable = 'spot_history_table'
}
